import { Box, Menu, ActionIcon, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'

import { useMutation } from '@redwoodjs/web'

import CompanyFeatureGate from 'src/components/CompanyFeatureGate/CompanyFeatureGate'
import AiAnalyzedIndicator from 'src/components/DataDisplay/AiAnalyzedIndicator/AiAnalyzedIndicator'
import ExpirationIndicator from 'src/components/DataDisplay/ExpirationIndicator/ExpirationIndicator'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import TrackerIconButton from 'src/components/DataDisplay/TrackerIconButton/TrackerIconButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import { CREATE_TRACKER_FOR_DOCUMENT } from 'src/graphql/expirationTrackers.graphql'
import IconThreeDots from 'src/icons/IconThreeDots'
import {
  canCreateTrackerForDocument,
  getActiveTrackerId,
  isStepTypeTracked,
} from 'src/lib/expirationTracking'
import { formatSnakeValue } from 'src/lib/formatters'
import { formatCalendarDate, toDayKey } from 'src/utils/date/toMonthYearKey'

const colorByStatus = {
  NOT_STARTED: 'gray',
  IN_PROGRESS: 'blue',
  PENDING_REVIEW: 'orange',
  COMPLETED: 'green',
  EXPIRED: 'red',
}

const DQFDocument = ({
  document,
  step,
  onPreview,
  onEdit,
  onDownload,
  onDelete,
  onRenewed,
  onOpenTracker,
  applicantInfo,
  refetchQueries,
}) => {
  const currentCompany = useCurrentCompanyContext()
  const showAiIndicators = currentCompany?.documentIntelligenceEnabled ?? false

  // When the document has no manual expiration date yet, fall back to the
  // AI-predicted DATE (if usable and not recruiter-rejected) so the urgency dot
  // still reflects expiry. This only feeds the dot a date — the AI involvement
  // itself is conveyed by the doc-level AiAnalyzedIndicator, so there is no AI
  // colour/sparkle on the dot.
  const aiPredictedExpiration = (() => {
    if (!showAiIndicators) return null
    const intel = document.documentIntelligence
    if (intel?.status !== 'COMPLETED') return null
    const pred = intel.predictions?.find((p) => p.field === 'DATE')
    if (!pred?.expirationValue || pred.reviewDecision === 'KEPT_ORIGINAL') {
      return null
    }
    return pred.expirationValue
  })()
  const indicatorDate = document.expirationDate ?? aiPredictedExpiration ?? null

  const [createTrackerForDocument] = useMutation(CREATE_TRACKER_FOR_DOCUMENT, {
    refetchQueries,
    onCompleted: () => toast('Expiration tracker created', 'success'),
    onError: (error) =>
      toast(error.message || 'Unable to create tracker', 'error'),
  })

  const canCreateTracker = canCreateTrackerForDocument({
    document,
    applicant: applicantInfo,
    expirationTrackingEnabled: currentCompany?.expirationTrackingEnabled,
  })

  const formatDateTime = (date) =>
    date ? dayjs(new Date(date)).format('MMM D, YYYY h:mm A') : null
  const formattedExpirationDate = formatCalendarDate(document.expirationDate)
  const formattedExpirationDateLong = formatCalendarDate(
    document.expirationDate,
    'MMM D, YYYY'
  )
  const formattedUploadDate = document.createdAt
    ? dayjs(new Date(document.createdAt)).format('MM/DD/YYYY')
    : null
  const isExpired = document.expirationDate
    ? dayjs(toDayKey(document.expirationDate))
        .startOf('day')
        .isBefore(dayjs().startOf('day'))
    : false

  return (
    <div className="flex flex-col border-b px-2 py-3" key={document.documentId}>
      <Box
        className="flex flex-row items-center gap-2 truncate text-sm font-medium text-doubleNickel-brand-500 hover:underline"
        onClick={() => onPreview(document)}
      >
        {document.fileName}
        {/* A single doc-level "AI analyzed" sparkle on every analyzed document —
            it marks that document intelligence ran, regardless of whether the
            output was an expiration date, a category, or grouped findings (MVR).
            The expiration dot below stays purely about expiration urgency (it may
            derive that urgency from an AI-predicted date via indicatorDate, but
            renders no AI colour of its own). */}
        {showAiIndicators && (
          <AiAnalyzedIndicator
            documentIntelligence={document.documentIntelligence}
          />
        )}
        <ExpirationIndicator
          expirationDate={indicatorDate}
          isRenewed={document.isRenewed}
          isTrackedCategory={isStepTypeTracked(step?.type)}
          variant="dot"
        />
      </Box>
      <div className="flex items-center justify-between gap-2 text-xs text-doubleNickel-gray-700">
        <div className="flex items-center gap-2">
          <Tooltip
            label={`Uploaded ${formatDateTime(document.createdAt)}${
              formattedExpirationDateLong
                ? ` | ${
                    isExpired ? 'Expired' : 'Expires'
                  } ${formattedExpirationDateLong}`
                : ''
            }`}
            withArrow
            position="top"
          >
            <span>
              Uploaded {formattedUploadDate}
              {formattedExpirationDate &&
                (isExpired
                  ? ` | Expired on ${formattedExpirationDate}`
                  : ` | Expires on ${formattedExpirationDate}`)}
            </span>
          </Tooltip>
          {getActiveTrackerId(document) && onOpenTracker && (
            <CompanyFeatureGate feature="expirationTrackingEnabled">
              <TrackerIconButton
                trackerId={getActiveTrackerId(document)}
                onClick={onOpenTracker}
              />
            </CompanyFeatureGate>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <StyledBadge
            textTransform="capitalize"
            color={colorByStatus[document.userStatus]}
          >
            {formatSnakeValue(document.userStatus)}
          </StyledBadge>
          <Menu shadow="md" position="bottom-end">
            <Menu.Target>
              <Tooltip label="Actions" withArrow position="top">
                <ActionIcon variant="subtle" className="h-5 w-5">
                  <IconThreeDots className="h-3 w-3 fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown className="w-40">
              <Menu.Item
                className="text-doubleNickel-gray-700"
                onClick={() => onPreview(document)}
              >
                View
              </Menu.Item>
              <Menu.Item
                className="text-doubleNickel-gray-700"
                onClick={() => onEdit(document, step)}
              >
                Edit
              </Menu.Item>
              <Menu.Item
                className="text-doubleNickel-gray-700"
                onClick={() => onDownload(document.documentId)}
              >
                Download
              </Menu.Item>
              {canCreateTracker && (
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  onClick={() =>
                    createTrackerForDocument({
                      variables: { documentId: document.documentId },
                    })
                  }
                >
                  Track expiration
                </Menu.Item>
              )}
              <Menu.Item
                className="text-doubleNickel-gray-700"
                onClick={() =>
                  onRenewed(document.documentId, !document.isRenewed)
                }
              >
                Mark as {document.isRenewed ? 'not renewed' : 'renewed'}
              </Menu.Item>
              <Menu.Item
                disabled={Boolean(document.voe)}
                className="text-doubleNickel-gray-700"
                onClick={() => onDelete(document.documentId, step)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </div>
    </div>
  )
}

export default DQFDocument
